import type { KeyboardEvent } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactDatePickerProps } from 'react-datepicker';
import ReactDatePicker from 'react-datepicker';
import { Box, Button, Input, Text } from '../index';
import TimeInput from '../TimeInput/TimeInput';
import TimeZoneInput from '../TimeInput/TimeZoneInput';
import moment from 'moment';
import useEvent from '../../hooks/useEvent';
import { useIsMobileView } from '../../hooks/useMediaQuery';
import { normalizeTimezone } from '../../../lib/moment-timezone-utils';

export const skewForDatePicker = (date: Date, timeZone: string): Date => {
  const normalized = normalizeTimezone(timeZone);
  const skewed = moment(date)
    .tz(normalized, false)
    .tz(moment.tz.guess(), true)
    .toDate();

  return skewed;
};

type TimePickerType = 'END_OF_DAY' | 'START_OF_DAY' | 'CUSTOM';

export interface DatePickerProps extends ReactDatePickerProps {
  defaultTimePickerType?: TimePickerType;
  hideTimePickerTypes?: boolean;
  showTime?: boolean;
  showTimeZoneInput?: boolean;
  timeZone: string | undefined;
  inputStyle?: React.CSSProperties;
  noButton?: boolean;
}

interface TimePickerProps {
  hideTypes: boolean;
  onChange: (date: Date) => void;
  onKeyPress?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onTypeChange?: (inputType: TimePickerType) => void;
  selectedType?: TimePickerType;
  timeZone?: string;
  value?: Date | null;
}

const getInitialTimePickerType = (value: Date, timeZone: string) => {
  const zonedValue = moment(value).tz(normalizeTimezone(timeZone), false);
  const hours = zonedValue.hours();
  const minutes = zonedValue.minutes();

  if (hours === 0 && minutes === 0) {
    return 'START_OF_DAY';
  } else if (hours === 23 && minutes === 59) {
    return 'END_OF_DAY';
  } else {
    return 'CUSTOM';
  }
};

const TimePicker = ({
  hideTypes,
  onChange,
  onKeyPress,
  onTypeChange,
  selectedType,
  timeZone = 'UTC',
  value,
}: TimePickerProps): React.ReactElement => {
  const handleRadioChange = (inputType: TimePickerType) => {
    onTypeChange?.(inputType);
  };

  return (
    <Box display="flex" flexDirection="column" mr={3}>
      <Box display="flex" mb={3} style={{ gap: '24px' }}>
        {!hideTypes || selectedType === 'START_OF_DAY' ? (
          <Box as="label" display="flex" alignItems="center" mb={2}>
            <input
              type="radio"
              name="timetype"
              value="START_OF_DAY"
              checked={selectedType === 'START_OF_DAY'}
              onChange={() => handleRadioChange('START_OF_DAY')}
              onKeyDown={onKeyPress}
            />{' '}
            <Text ml={1}>0:00</Text>
          </Box>
        ) : null}
        {!hideTypes || selectedType === 'END_OF_DAY' ? (
          <Box as="label" display="flex" alignItems="center" mb={2}>
            <input
              type="radio"
              name="timetype"
              value="END_OF_DAY"
              checked={selectedType === 'END_OF_DAY'}
              onChange={() => handleRadioChange('END_OF_DAY')}
              onKeyDown={onKeyPress}
            />{' '}
            <Text ml={1}>23:59</Text>
          </Box>
        ) : null}
        {!hideTypes ? (
          <Box as="label" display="flex" alignItems="center" mb={2}>
            <input
              type="radio"
              name="timetype"
              value="CUSTOM"
              checked={selectedType === 'CUSTOM'}
              onChange={() => handleRadioChange('CUSTOM')}
              onKeyDown={onKeyPress}
            />{' '}
            <Text ml={1}>Custom time</Text>
          </Box>
        ) : null}
      </Box>
      <Box>
        <TimeInput
          value={value || undefined}
          disabled={selectedType !== 'CUSTOM'}
          onChange={onChange}
          onKeyPress={onKeyPress}
          timeZone={timeZone}
        />
      </Box>
    </Box>
  );
};

const formatTzDate = (
  date: Date | null | undefined,
  timeZone: string,
  format: string,
) => {
  if (!date) {
    return '';
  }
  return moment(date).tz(timeZone, false).format(format);
};

const CustomDatePickerInput = React.memo(
  React.forwardRef(
    (
      {
        dateFormat,
        selected,
        customOnChange,
        onKeyPress,
        style,
        onClick,
      }: {
        onClick?: () => void;
        onKeyPress: (event: KeyboardEvent<HTMLInputElement>) => void;
        selected: string;
        dateFormat: string;
        customOnChange: (date: Date | null) => void;
        style: React.CSSProperties;
      },
      ref: React.Ref<HTMLInputElement>,
    ): React.ReactElement => {
      const [internalVal, setInternalVal] = useState<string>(selected);
      const isMobileView = useIsMobileView();

      useEffect(() => {
        setInternalVal(selected);
      }, [selected]);

      const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInternalVal(event.target.value);
      };

      const onBlur = () => setInternalVal(selected);

      const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
        onKeyPress?.(event);
        if (['Enter', 'Tab'].includes(event.key)) {
          event.stopPropagation();
          if (internalVal.length === 0) customOnChange(null);

          const formats = [dateFormat, 'DD.MM.YYYY', 'D.M.YYYY'];
          const parsed = moment(internalVal, formats, true);

          if (parsed.isValid()) {
            customOnChange(parsed.toDate());
          }
        }
      };

      return (
        <Input
          style={style}
          onClick={onClick}
          ref={ref}
          onKeyDown={handleKeyPress}
          value={internalVal}
          onChange={onChange}
          onBlur={onBlur}
          autoComplete="off"
          onFocus={(e) => (isMobileView ? e.target.blur() : null)}
        />
      );
    },
  ),
);

const constrain = (
  min: Date | null | undefined,
  max: Date | null | undefined,
  value: Date | null,
) => {
  if (!value) {
    return value;
  } else if (min && min > value) {
    return min;
  } else if (max && max < value) {
    return max;
  } else {
    return value;
  }
};

const defaultInputStyle = {};

const DatePicker = React.memo(
  ({
    children,
    defaultTimePickerType = 'START_OF_DAY',
    hideTimePickerTypes = false,
    onChange,
    selected,
    showTime = true,
    showTimeInput,
    showTimeZoneInput = false,
    timeZone = 'UTC',
    isClearable = true,
    noButton = false,
    inputStyle = defaultInputStyle,
    maxDate,
    minDate,
    ...restProps
  }: DatePickerProps): React.ReactElement => {
    const initialInputType = useMemo(() => {
      return hideTimePickerTypes
        ? defaultTimePickerType
        : selected
          ? getInitialTimePickerType(selected, timeZone)
          : defaultTimePickerType;
    }, [defaultTimePickerType, hideTimePickerTypes, selected, timeZone]);

    const datepickerRef = useRef<ReactDatePicker>(null);

    const [value, setValueState] = useState<Date | null>(selected || null);
    const [selectedTimePickerType, setSelectedTimePickerType] = useState<
      TimePickerType | undefined
    >(initialInputType);

    const setValue = useEvent((value: Date | null) => {
      setValueState(constrain(minDate, maxDate, value));
    });

    useEffect(() => {
      setValue(selected || null);
    }, [selected, setValue]);

    useEffect(
      () => setSelectedTimePickerType(initialInputType),
      [initialInputType],
    );

    const [selectedTimeZone, setSelectedTimeZone] = useState<string>(
      normalizeTimezone(timeZone),
    );

    useEffect(() => {
      if (!showTimeZoneInput) {
        setSelectedTimeZone(normalizeTimezone(timeZone));
      }
    }, [showTimeZoneInput, timeZone]);

    const dateFormat = useMemo(
      () => `DD.MM.yyyy${showTime ? ' HH:mm' : ''}`,
      [showTime],
    );

    const handleChange = useCallback(
      (date: Date | [Date, Date] | null, isManualChange: boolean): void => {
        const _date: Date | null =
          date instanceof Array && date[0] instanceof Date
            ? date[0]
            : date instanceof Date
              ? date
              : null;

        if (!_date) {
          setValue(_date);
          if (isManualChange) {
            onChange(_date, undefined);
          }
          return;
        }

        const validDate = constrain(minDate, maxDate, _date);

        const zonedMoment = moment(validDate).tz(selectedTimeZone, true);

        if (!isManualChange) {
          switch (selectedTimePickerType) {
            case 'START_OF_DAY':
              zonedMoment.startOf('day');
              break;
            case 'END_OF_DAY':
              zonedMoment.endOf('day');
              break;
          }
        }

        const newDate = zonedMoment.toDate();

        setValue(newDate);
        if (isManualChange) {
          onChange(newDate, undefined);
        }
      },
      [
        minDate,
        maxDate,
        selectedTimeZone,
        setValue,
        onChange,
        selectedTimePickerType,
      ],
    );

    const handleTimePickerTypeChange = useCallback(
      (timeInputType: TimePickerType): void => {
        const newDate = value ? new Date(value) : new Date();
        const zonedDate = moment(newDate).tz(selectedTimeZone, false);

        if (timeInputType === 'START_OF_DAY') {
          const realDateStart = zonedDate.startOf('day').toDate();

          setValue(realDateStart);
        } else if (timeInputType === 'END_OF_DAY') {
          const realDateStart = zonedDate.endOf('day').toDate();

          setValue(realDateStart);
        }

        setSelectedTimePickerType(timeInputType);
      },
      [selectedTimeZone, setValue, value],
    );

    const handleClear = useCallback(() => {
      onChange(null, undefined);
      datepickerRef.current?.setOpen?.(false);
    }, [onChange]);

    const handleInputChange = useEvent((date: Date | null) => {
      handleChange(date, true);
      datepickerRef.current?.setOpen?.(false);
    });

    const selectedDate = useMemo(
      () => formatTzDate(selected, selectedTimeZone, dateFormat),
      [selected, selectedTimeZone, dateFormat],
    );

    const handleConfirm = useEvent(() => {
      onChange(value, undefined);
      datepickerRef.current?.setOpen?.(false);
    });

    const handleKeyPress = useEvent(
      (event: KeyboardEvent<HTMLInputElement | HTMLDivElement>) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          setValue(selected || null);
          datepickerRef.current?.setOpen?.(false);
        }
        if (event.key === 'Enter') {
          event.stopPropagation();
          datepickerRef.current?.setOpen?.(false);
        }
      },
    );

    return (
      <Box
        minWidth="100%"
        height="100%"
        className="react-datepicker-container"
        onKeyDown={handleKeyPress}
      >
        <ReactDatePicker
          ref={datepickerRef}
          customInput={
            <CustomDatePickerInput
              dateFormat={dateFormat}
              onKeyPress={handleKeyPress}
              selected={selectedDate}
              customOnChange={handleInputChange}
              style={inputStyle}
            />
          }
          showTimeInput
          customTimeInput={<div style={{ display: 'none' }} />}
          timeInputLabel=""
          onChange={(d) => handleChange(d, true)}
          selected={value ? skewForDatePicker(value, selectedTimeZone) : null}
          shouldCloseOnSelect={false}
          onKeyDown={handleKeyPress}
          maxDate={maxDate}
          minDate={minDate}
          {...restProps}
          fixedHeight
        >
          <Box pb={3} px={3}>
            {showTimeInput ? (
              <Box display="flex" justifyContent="center" mb={3}>
                <TimePicker
                  hideTypes={hideTimePickerTypes}
                  onChange={(date) => {
                    setValue(date);
                    onChange(date, undefined);
                  }}
                  onKeyPress={handleKeyPress}
                  timeZone={selectedTimeZone}
                  onTypeChange={handleTimePickerTypeChange}
                  selectedType={selectedTimePickerType}
                  value={value}
                />
              </Box>
            ) : null}
            <Box display="flex" flexDirection="row" alignItems="flex-end">
              {isClearable ? (
                <Button
                  variant="secondary"
                  onPress={handleClear}
                  flexGrow={1}
                  flexBasis={0}
                  mr={3}
                >
                  Clear
                </Button>
              ) : null}
              {!noButton ? (
                <Button
                  disabled={value === null}
                  variant="primary"
                  onPress={handleConfirm}
                  flexGrow={1}
                  flexBasis={0}
                >
                  OK
                </Button>
              ) : null}
            </Box>
            {showTimeZoneInput ? (
              <Box mt={showTimeInput ? 2 : 0}>
                <Box px={3} mb={3}>
                  <TimeZoneInput
                    value={selectedTimeZone}
                    onChange={(timeZone: string) => {
                      setSelectedTimeZone(timeZone);
                    }}
                  />
                </Box>
              </Box>
            ) : null}
            <Box>{children}</Box>
          </Box>
        </ReactDatePicker>
      </Box>
    );
  },
);

export default DatePicker;
